/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { FilePicker } from 'src/core/components/FilePicker';
import { useFormState } from 'src/core/hooks/useFormState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { FormValidators } from 'src/core/utils/formValidators';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';

export type AppLogsImportModalDto = {
    /**
     * The logs file to import.
     */
    file: File;
};

const messages = defineMessages({
    modalTitle: {
        id: 'apps.appLogsImportModal.modalTitle',
        defaultMessage: 'Import endpoint logs'
    },
    fileLabel: {
        id: 'apps.appLogsImportModal.fileLabel',
        defaultMessage: 'Choose Logs File (CSV format)'
    },
    hint: {
        id: 'apps.appLogsImportModal.hint',
        defaultMessage: 'Use endpoint logs to improve your application through active learning.'
    }
});

/**
 * Represents a modal to import a new version.
 *
 * @param props The properties passed to the modal.
 */
export const AppLogsImportModal = withModalMounter((props: BasicModalProps<AppLogsImportModalDto>) => {
    const { onSubmit } = props;
    const { localize } = useLocalization();
    const { formState, formStateDto, updateFormState } = useFormState<AppLogsImportModalDto>({
        file: [
            new Blob([], { type: 'file' }) as File,
            FormValidators.createRequiredValidator(),
            FormValidators.createExtensionValidator('csv')
        ]
    });

    return (
        <BasicModal
            {...props}
            valid={formState.form.valid}
            onSubmit={() => onSubmit(formStateDto)}
            dialogContentProps={{ title: localize(messages.modalTitle) }}>
            <Stack tokens={{ childrenGap: 20 }}>
                <FilePicker
                    autoFocus
                    required
                    label={localize(messages.fileLabel)}
                    errorMessage={formState.fields.file.errorMessage}
                    onFilesPicked={files => updateFormState({ file: files.item(0) })}
                />

                <Text>{localize(messages.hint)}</Text>
            </Stack>
        </BasicModal>
    );
});
